import { useEffect, useState } from 'react';
import { Badge, Card, Text } from '@tremor/react';
import { fetchReports } from '../api/reports';
import { CHART_CATALOG_KEYS, chartCatalogLabel } from '../lib/dashboardCatalog';
import { t } from '../lib/i18n';
import type { DashboardWidget, Report } from '../api/types';

interface Props {
  /** Widgets already on the board — hides matching chart keys / report ids so
   *  nothing gets added twice. */
  currentWidgets: DashboardWidget[];
  onAdd: (widget: DashboardWidget) => void;
}

let nextIdSeq = 0;
/** Widget ids only need to be unique within one layout; a monotonic counter
 *  keyed to page load is simpler than a UUID dep for this. */
function newWidgetId(prefix: string): string {
  nextIdSeq += 1;
  return `${prefix}-${Date.now()}-${nextIdSeq}`;
}

/**
 * "Add a widget" panel (JCD-17 phase 3) — the chart catalog (fixed 8 keys)
 * plus the user's saved reports (server already filters to what they can
 * see, same list /reports itself uses). Click to add; already-added sources
 * are hidden, mirroring ColumnPicker's "available" row.
 */
export default function DashboardWidgetPicker({ currentWidgets, onAdd }: Props) {
  const [reports, setReports] = useState<Report[] | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchReports().then((r) => { if (alive) setReports(r); }).catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, []);

  const et = t.tablero.edit;
  const usedChartKeys = new Set(currentWidgets.filter((w) => w.kind === 'chart').map((w) => w.source));
  const usedReportIds = new Set(currentWidgets.filter((w) => w.kind === 'report').map((w) => w.source));
  const availableCharts = CHART_CATALOG_KEYS.filter((k) => !usedChartKeys.has(k));
  const availableReports = (reports ?? []).filter((r) => !usedReportIds.has(r.id));

  return (
    <Card>
      <Text className="font-semibold">{et.addWidget}</Text>

      <Text className="text-tremor-content text-xs mt-3 mb-1">{et.chartsSection}</Text>
      {availableCharts.length === 0 ? (
        <Text className="text-tremor-content text-xs italic">{et.alreadyAdded}</Text>
      ) : (
        <div className="flex flex-wrap gap-1">
          {availableCharts.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => onAdd({ id: newWidgetId('w'), kind: 'chart', source: key })}
            >
              <Badge color="gray">+ {chartCatalogLabel(key)}</Badge>
            </button>
          ))}
        </div>
      )}

      <Text className="text-tremor-content text-xs mt-3 mb-1">{et.reportsSection}</Text>
      {err ? (
        <Text className="text-tremor-content text-xs italic">{t.tablero.reportUnavailable}</Text>
      ) : availableReports.length === 0 ? (
        <Text className="text-tremor-content text-xs italic">{et.noReports}</Text>
      ) : (
        <div className="flex flex-wrap gap-1">
          {availableReports.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => onAdd({ id: newWidgetId('w'), kind: 'report', source: r.id, title: r.name })}
            >
              <Badge color="gray">+ {r.name}</Badge>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}
